// „Eksportuj do Excela” of „Przekrój sprzedaży”: the table exactly as the
// screen shows it (same rows, levels, columns and measure), as the payload of
// POST /reports/sales-pivot/export. The backend only lays it out.
import { measureIncomplete, measureValue, type PivotCells, type PivotMeasure, type PivotNode, type PivotOrderLeaf, type PivotResult } from './orderPivot';
import type { PivotViewState } from './pivotViewState';

export type PivotExportFormat = 'text' | 'money' | 'percent' | 'count';

export interface PivotExportTable {
  title: string;
  /** Sheet name (max 31 characters, no : \\ / ? * [ ]); the backend's default otherwise. */
  sheetName?: string;
  info: string[];
  columns: { header: string; format: PivotExportFormat }[];
  /** `marked`: cell indexes holding an incomplete cost — „*” in the file, like the screen. */
  rows: { kind: 'group' | 'order' | 'total'; level: number; cells: (string | number | null)[]; marked?: number[] }[];
}

/** A valid Excel sheet name from a title: forbidden characters out, at most 31 characters. */
export function exportSheetName(title: string): string {
  return title.replace(/[:\\/?*[\]']/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31).trim() || 'Arkusz';
}

export interface PivotExportLabels {
  title: string;
  info: string[];
  labelHeader: string;
  bucket: (bucket: string) => string;
  total: string;
  orders: string;
  invoiced: string;
  waiting: string;
  cost: string;
  margin: string;
  marginPercent: string;
  orderLabel: (leaf: PivotOrderLeaf) => string;
}

const MEASURE_FORMAT: Record<PivotMeasure, PivotExportFormat> = {
  revenue: 'money', cost: 'money', margin: 'money', marginPercent: 'percent', orders: 'count',
};

// Whole złoty and one decimal of a percent, as on the screen.
function rounded(value: number | null, format: PivotExportFormat): number | null {
  if (value == null) return null;
  return format === 'percent' ? Math.round(value * 10) / 10 : Math.round(value);
}

export function buildPivotExportTable(result: PivotResult, view: PivotViewState, labels: PivotExportLabels): PivotExportTable {
  const byPeriod = view.columns !== 'total' && result.buckets.length > 0;
  const format = MEASURE_FORMAT[view.measure];
  const columns: PivotExportTable['columns'] = byPeriod
    ? [
      { header: labels.labelHeader, format: 'text' },
      ...result.buckets.map((bucket) => ({ header: labels.bucket(bucket), format })),
      { header: labels.total, format },
    ]
    : [
      { header: labels.labelHeader, format: 'text' },
      { header: labels.orders, format: 'count' },
      { header: labels.invoiced, format: 'money' },
      { header: labels.waiting, format: 'money' },
      { header: labels.cost, format: 'money' },
      { header: labels.margin, format: 'money' },
      { header: labels.marginPercent, format: 'percent' },
    ];

  const cells = (label: string, source: PivotCells, isOrder: boolean): (string | number | null)[] => {
    if (byPeriod) {
      return [
        label,
        ...result.buckets.map((bucket) => rounded(measureValue(source.byBucket.get(bucket), view.measure), format)),
        rounded(measureValue(source.total, view.measure), format),
      ];
    }
    const money = source.total;
    return [
      label,
      isOrder ? null : money.orderIds.size,
      rounded(money.invoiced, 'money'),
      rounded(money.waiting, 'money'),
      rounded(money.cost, 'money'),
      rounded(money.margin, 'money'),
      rounded(measureValue(money, 'marginPercent'), 'percent'),
    ];
  };

  // The same cells the screen marks with „*”.
  const marked = (source: PivotCells): number[] => {
    if (byPeriod) {
      return [...result.buckets.map((bucket) => source.byBucket.get(bucket)), source.total]
        .map((money, index) => (measureIncomplete(money, view.measure) ? index + 1 : -1))
        .filter((index) => index > 0);
    }
    const money = source.total;
    const list: number[] = [];
    if (money.marginIncomplete) list.push(4, 5);
    if (money.pendingOrderIds.size > 0 && measureValue(money, 'marginPercent') != null) list.push(6);
    return list;
  };
  const row = (kind: 'group' | 'order' | 'total', level: number, label: string, source: PivotCells, isOrder: boolean) => {
    const marks = marked(source);
    return { kind, level, cells: cells(label, source, isOrder), ...(marks.length ? { marked: marks } : {}) };
  };

  const rows: PivotExportTable['rows'] = [];
  const walk = (node: PivotNode, level: number) => {
    rows.push(row('group', level, node.label, node, false));
    node.children.forEach((child) => walk(child, level + 1));
    node.orders.forEach((leaf) => rows.push(row('order', level + 1, labels.orderLabel(leaf), leaf, true)));
  };
  result.nodes.forEach((node) => walk(node, 0));
  rows.push(row('total', 0, labels.total, result, false));

  return { title: labels.title, info: labels.info, columns, rows };
}
